import React, { useState, useEffect } from 'react';
import { RiCloseLine } from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminMaterial } from '@/hooks/admin/useAdmin';
import { useCreateMaterial, useUpdateMaterial } from '@/hooks/admin/useAdminMaterials';

interface MaterialModalProps {
    isOpen: boolean;
    onClose: () => void;
    material?: AdminMaterial;
    onSuccess?: () => void;
}

const MaterialModal: React.FC<MaterialModalProps> = ({
    isOpen,
    onClose,
    material,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        additional_cost: 0,
        is_active: true
    });

    const createMaterial = useCreateMaterial();
    const updateMaterial = useUpdateMaterial();

    const isEditing = !!material;
    const isLoading = createMaterial.isPending || updateMaterial.isPending;

    useEffect(() => {
        if (material) {
            setFormData({
                name: material.name,
                description: material.description || '',
                additional_cost: material.additional_cost,
                is_active: material.is_active
            });
        } else {
            setFormData({
                name: '',
                description: '',
                additional_cost: 0,
                is_active: true
            });
        }
    }, [material, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert('El nombre del material es requerido');
            return;
        }

        if (formData.additional_cost < 0) {
            alert('El costo adicional debe ser mayor o igual a cero');
            return;
        }

        try {
            if (isEditing && material) {
                await updateMaterial.mutateAsync({
                    id: material.id,
                    data: formData
                });
            } else {
                await createMaterial.mutateAsync(formData);
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error saving material:', error);
            alert('Error al guardar el material');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal-content">
                <div className="admin-modal-header">
                    <h2 className="admin-modal-title">
                        {isEditing ? 'Editar Material' : 'Nuevo Material'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="admin-modal-close"
                        disabled={isLoading}
                    >
                        <RiCloseLine />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="admin-modal-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <Input
                                label="Nombre"
                                name="name"
                                value={formData.name}
                                setValue={(value) => setFormData(prev => ({ ...prev, name: value }))}
                                placeholder="Ingrese el nombre del material"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <Input
                                label="Costo Adicional"
                                name="additional_cost"
                                type="number"
                                step="0.01"
                                value={formData.additional_cost.toString()}
                                setValue={(value) => setFormData(prev => ({ ...prev, additional_cost: parseFloat(value) || 0 }))}
                                placeholder="0.00"
                            />
                        </div>

                        <div className="admin-form-group admin-form-full">
                            <Input
                                label="Descripción"
                                name="description"
                                value={formData.description}
                                setValue={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                placeholder="Descripción del material (opcional)"
                                rows={3}
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                />
                                <span>Material Activo</span>
                            </label>
                        </div>
                    </div>

                    <div className="admin-modal-actions">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading}
                        >
                            {isLoading ? <Spinner size="sm" /> : null}
                            {isEditing ? 'Actualizar' : 'Crear'} Material
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MaterialModal;
